import { Grid, IconButton, makeStyles, TextField, Tooltip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import LambdaIcon from '../../../../assets/aws/lambda.svg';
import type { ValkyrieLambda } from 'config-editor-common';
import {
  LAMBDA_MEMORY_MB_INPUT_PROPS,
  LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS,
} from 'config-editor-common';
import { LambdaNameInputRef } from '../../../LambdaComponents/shared/LambdaNameInput';
import { ValkyrieEnvVarsSection } from './ValkyrieEnvVarsSection';
import { ValkyrieSqsQueueSelector } from './ValkyrieSqsQueueSelector';

// AWS Lambda orange colors (match phx-api LambdaItemEditor)
const LAMBDA_ORANGE = '#FF9900';
const LAMBDA_ORANGE_LIGHT = '#FFB84D';

const useStyles = makeStyles(theme => ({
  lambdaItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid ${LAMBDA_ORANGE_LIGHT}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    position: 'relative',
    '&:hover': {
      boxShadow: theme.shadows[2],
      borderColor: LAMBDA_ORANGE,
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  nameContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  lambdaIcon: {
    width: '1.25rem',
    height: '1.25rem',
    flexShrink: 0,
  },
}));

interface ValkyrieLambdaItemEditorProps {
  name: string;
  lambda: ValkyrieLambda;
  onUpdate: (name: string, field: keyof ValkyrieLambda, value: unknown) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (name: string) => void;
  /** Queue names for sqs_queue_urls selector */
  queueNames?: string[];
}

export const ValkyrieLambdaItemEditor = forwardRef<
  LambdaNameInputRef,
  ValkyrieLambdaItemEditorProps
>(({ name, lambda, onUpdate, onRename, onDelete, queueNames = [] }, ref) => {
  const classes = useStyles();
  const [nameInput, setNameInput] = useState(name);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      requestAnimationFrame(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      });
    },
  }));

  const handleRename = () => {
    if (nameInput.trim() && nameInput !== name) {
      onRename(name, nameInput.trim());
    }
  };

  return (
    <div className={classes.lambdaItem} data-ai-anchor={`lambda.${name}.settings`}>
      <div className={classes.headerRow}>
        <div className={classes.nameContainer}>
          <img src={LambdaIcon} alt="Lambda" className={classes.lambdaIcon} />
          <TextField
            inputRef={nameInputRef}
            label="Lambda Name"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onBlur={handleRename}
            fullWidth
            variant="outlined"
            size="small"
          />
        </div>
        <Tooltip title="Delete Lambda">
          <IconButton
            size="small"
            onClick={() => onDelete(name)}
            color="secondary"
            tabIndex={-1}
            data-ai-anchor={`lambda.${name}.delete`}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Lambda Handler"
            value={lambda.lambda_handler || ''}
            onChange={e => onUpdate(name, 'lambda_handler', e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Namespace::Class::Method"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Lambda Trigger Path"
            value={lambda.lambda_trigger_path || ''}
            onChange={e => onUpdate(name, 'lambda_trigger_path', e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="e.g., */GET/events"
            helperText="API path pattern (e.g., */GET/events, */POST/events/*)"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Timeout (s)"
            type="number"
            value={lambda.timeout ?? ''}
            onChange={e =>
              onUpdate(name, 'timeout', e.target.value ? parseInt(e.target.value, 10) : undefined)
            }
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Override default"
            inputProps={{ ...LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Memory (MB)"
            type="number"
            value={lambda.memory_size ?? ''}
            onChange={e =>
              onUpdate(
                name,
                'memory_size',
                e.target.value ? parseInt(e.target.value, 10) : undefined,
              )
            }
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Override default"
            inputProps={{ ...LAMBDA_MEMORY_MB_INPUT_PROPS }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ValkyrieSqsQueueSelector
            value={Array.isArray(lambda.sqs_send_policies) ? lambda.sqs_send_policies : []}
            onChange={v => onUpdate(name, 'sqs_send_policies', v)}
            queueNames={queueNames}
            label="SQS Send Policies"
            helperText="Queue this lambda can send messages to"
          />
        </Grid>
      </Grid>

      <ValkyrieEnvVarsSection
        envVars={lambda.env_vars ?? {}}
        sqsQueueUrls={lambda.sqs_queue_urls ?? {}}
        onEnvVarsChange={vars => onUpdate(name, 'env_vars', vars)}
        onSqsQueueUrlsChange={urls => onUpdate(name, 'sqs_queue_urls', urls)}
        queueNames={queueNames}
      />
    </div>
  );
});

ValkyrieLambdaItemEditor.displayName = 'ValkyrieLambdaItemEditor';
